import { useParams } from "react-router";

import { getComboTitle } from "@datanac/datanac-api-toolkit/dist/insurance/optimizer/optimizer";
import { Autocomplete, Box, Divider, FormControl, Grid, Stack, TextField, Typography } from "@mui/material";
import {
  MASK_NUMERIC,
  MASK_PERCENTAGE,
  StatefulControlledTextField,
} from "components/Core/StatefulControlledTextField";
import { TilleyDatePicker } from "components/Core/TilleyDatePicker";
import { InsuranceIcon } from "components/Icons/Icons";
import { useBlockerPrompt } from "components/Menu/hooks/useBlockerPrompt";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import dayjs from "dayjs";

import { claimPaymentAmount, INSURANCE_CLAIM_DAMAGE_CAUSES, INSURANCE_CLAIM_STAGE_NAMES } from "./InsuranceService";
import { useInsuranceClaimForm } from "./useInsuranceClaimForm";

export default function InsurancePlanClaimEditWorkspace() {
  const { insurance_claim_id } = useParams();

  const formMaxWidth = "1000px";

  const {
    state,
    insurancePlans,
    formStatusComponent,
    formStatusState,
    handleChangeStateSelect,
    handleChangeState,
    locationPickers,
  } = useInsuranceClaimForm();

  const getInsurancePlans = (insurancePlan) =>
    insurancePlan?.insurance_plan_name +
    " - " +
    insurancePlan?.unit_structure_code +
    " - " +
    getComboTitle([insurancePlan]);

  useBlockerPrompt(Boolean(formStatusState?.isFormDirty));

  const headerTitle =
    String(insurance_claim_id) === "0"
      ? "Add Claim"
      : state?.insurance_plan_name + " - " + dayjs(state?.first_damage_date).format("MM/DD/YYYY");

  const pickerFields = [
    { key: "state", label: "State", options: locationPickers.stateOptions, value: locationPickers.stateName },
    { key: "county", label: "County", options: locationPickers.countyOptions, value: locationPickers.countyName },
    {
      key: "commodity",
      label: "Commodity",
      options: locationPickers.commodityOptions,
      value: locationPickers.commodityName,
    },
    { key: "type", label: "Type", options: locationPickers.typeOptions, value: locationPickers.typeName },
    {
      key: "practice",
      label: "Practice",
      options: locationPickers.practiceOptions,
      value: locationPickers.practiceName,
    },
  ];

  return (
    <>
      <WorkspaceHeader
        title={headerTitle}
        category={{
          title: "Insurance",
          icon: <InsuranceIcon />,
        }}
        breadCrumbs={[
          { title: "Claims", path: "/insurance/claims" },
          { title: headerTitle, path: `/insurance/claims/${insurance_claim_id}` },
        ]}
      />

      <Stack direction="row" spacing={2} className="headerWithMenuButton" justifyContent="flex-end">
        {formStatusComponent}
      </Stack>

      <Box sx={{ maxWidth: formMaxWidth }}>
        <Grid container spacing={1} rowGap={2}>
          {pickerFields.map(({ key, label, options, value }) => (
            <Grid item xs={12} md={4} lg={2} key={key} className="autocomplete_item">
              <FormControl fullWidth>
                <Autocomplete
                  value={value}
                  onChange={(_, newValue) => locationPickers.setField(key, newValue)}
                  options={options}
                  autoHighlight
                  renderInput={(params) => (
                    <TextField {...params} label={label} name={key} InputLabelProps={{ shrink: true }} />
                  )}
                />
              </FormControl>
            </Grid>
          ))}

          <Grid item xs={12} md={4} lg={6} className="autocomplete_item">
            <FormControl fullWidth sx={{ maxWidth: "100%" }}>
              <Autocomplete
                value={insurancePlans.find((ip) => ip.id === state.insurance_plan_id) || null}
                onChange={(event, newValue) => {
                  handleChangeStateSelect({ target: { name: "insurance_plan", value: newValue } });
                }}
                autoSelect
                autoHighlight
                options={insurancePlans}
                getOptionLabel={(option) => getInsurancePlans(option) || ""}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Insurance Plan"
                    name="insurance_plan"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <FormControl fullWidth>
              <StatefulControlledTextField
                label="AIP Contact Name"
                name="aip_contact_name"
                defaultValue={state.aip_contact_name || ""}
                variant="outlined"
                onChange={handleChangeState}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4} lg={3} className="claim_date_grid">
            <TilleyDatePicker
              name="insured_loss_signature_date"
              label="Insured Loss Date"
              value={state?.insured_loss_signature_date}
              onChange={handleChangeState}
              className="insured_loss_signature_date_picker"
              slotProps={{
                popper: {
                  "data-testid": "insured-loss-date-picker-dialog",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={4} lg={3} className="claim_date_grid">
            <TilleyDatePicker
              name="adjuster_signature_date"
              label="Adjuster Date"
              value={state?.adjuster_signature_date}
              onChange={handleChangeState}
              className="adjuster_signature_date_picker"
              slotProps={{
                popper: {
                  "data-testid": "adjuster-date-picker-dialog",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={4} lg={3} className="autocomplete_item">
            <FormControl fullWidth>
              <Autocomplete
                value={state?.stage_name || null}
                onChange={(event, newValue) => {
                  handleChangeStateSelect({ target: { name: "stage_name", value: newValue } });
                }}
                autoSelect
                autoHighlight
                options={INSURANCE_CLAIM_STAGE_NAMES}
                getOptionLabel={(option) => option || ""}
                renderInput={(params) => (
                  <TextField {...params} label="Stage Name" name="stage_name" InputLabelProps={{ shrink: true }} />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <FormControl fullWidth>
              <StatefulControlledTextField
                label="Policy Number"
                name="policy_number"
                defaultValue={state.policy_number || ""}
                onChange={handleChangeState}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12} md={12}>
            <Divider />
            <Typography variant="h2">Damage</Typography>
          </Grid>
          <Grid item xs={12} md={4} lg={3} className="autocomplete_item">
            <FormControl fullWidth>
              <Autocomplete
                value={state?.first_damage_cause_name || null}
                onChange={(event, newValue) => {
                  handleChangeStateSelect({ target: { name: "first_damage_cause_name", value: newValue } });
                }}
                autoSelect
                autoHighlight
                options={INSURANCE_CLAIM_DAMAGE_CAUSES}
                getOptionLabel={(option) => option || ""}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="First Damage Cause Name"
                    name="first_damage_cause_name"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4} lg={3} className="claim_date_grid">
            <TilleyDatePicker
              name="first_damage_date"
              label="First Damage Date"
              value={state?.first_damage_date}
              onChange={handleChangeState}
              className="first_damage_date_picker"
              slotProps={{
                popper: {
                  "data-testid": "first-damage-date-picker-dialog",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <FormControl fullWidth>
              <StatefulControlledTextField
                label="First Damage Percent %"
                name="first_damage_percent"
                defaultValue={state.first_damage_percent || null}
                onChange={handleChangeState}
                maskType={MASK_PERCENTAGE}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4} lg={3} className="autocomplete_item">
            <FormControl fullWidth>
              <Autocomplete
                value={state?.second_damage_cause_name || null}
                onChange={(event, newValue) => {
                  handleChangeStateSelect({ target: { name: "second_damage_cause_name", value: newValue } });
                }}
                autoSelect
                autoHighlight
                options={INSURANCE_CLAIM_DAMAGE_CAUSES}
                getOptionLabel={(option) => option || ""}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Second Damage Cause Name"
                    name="second_damage_cause_name"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4} lg={3} className="claim_date_grid">
            <TilleyDatePicker
              name="second_damage_date"
              label="Second Damage Date"
              value={state?.second_damage_date}
              onChange={handleChangeState}
              className="second_damage_date_picker"
              slotProps={{
                popper: {
                  "data-testid": "second-damage-date-picker-dialog",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <FormControl fullWidth>
              <StatefulControlledTextField
                label="Second Damage Percent %"
                name="second_damage_percent"
                defaultValue={state.second_damage_percent || null}
                variant="outlined"
                onChange={handleChangeState}
                maskType={MASK_PERCENTAGE}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={12}>
            <Divider />
            <Typography variant="h2">Harvest</Typography>
          </Grid>

          <Grid item xs={12} md={4} lg={3}>
            <FormControl fullWidth>
              <StatefulControlledTextField
                label="Harvested Production Quantity"
                name="harvested_production_quantity"
                defaultValue={state.harvested_production_quantity || 0}
                variant="outlined"
                onChange={handleChangeState}
                maskType={MASK_NUMERIC}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <FormControl fullWidth>
              <StatefulControlledTextField
                label="Production To Count Quantity"
                name="production_to_count_quantity"
                defaultValue={state.production_to_count_quantity || 0}
                variant="outlined"
                onChange={handleChangeState}
                maskType={MASK_NUMERIC}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <FormControl fullWidth>
              <StatefulControlledTextField
                label="Payment Amount"
                name="payment_amount"
                defaultValue={claimPaymentAmount(state)}
                variant="outlined"
                onChange={handleChangeState}
                maskType={MASK_NUMERIC}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4} lg={3} className="claim_date_grid">
            <TilleyDatePicker
              name="payment_date"
              label="Payment Date"
              value={state?.payment_date}
              onChange={handleChangeState}
              className="payment_date_picker"
              slotProps={{
                popper: {
                  "data-testid": "payment-date-picker-dialog",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={4} lg={6}>
            <FormControl fullWidth>
              <StatefulControlledTextField
                label="Note"
                name="note"
                defaultValue={state.note || ""}
                variant="outlined"
                onChange={handleChangeState}
                inputProps={{ maxLength: 100 }}
              />
            </FormControl>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}
